import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Code, CheckCircle, XCircle, Clock, ArrowLeft } from 'lucide-react'
import { developerApplicationAPI, userAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import DashboardHeader from '../components/DashboardHeader'
import DashboardFooter from '../components/DashboardFooter'
import toast from 'react-hot-toast'

export default function ApplyDeveloper() {
  const navigate = useNavigate()
  const { updateUser } = useAuthStore()
  const [formData, setFormData] = useState({
    reason: '',
    experience: '',
    portfolio_url: '',
    github_url: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Fetch existing application
  const { data: application, isLoading, refetch } = useQuery({
    queryKey: ['my-developer-application'],
    queryFn: async () => {
      const { data } = await developerApplicationAPI.getMyApplication()
      return data
    },
  })

  // Refresh user profile if application is approved (to update role in auth store)
  useEffect(() => {
    if (application?.status === 'approved') {
      const refreshUserProfile = async () => {
        try {
          const { data } = await userAPI.getProfile()
          console.log('Refreshed user profile:', data)
          // Backend returns user directly, not wrapped in { user: ... }
          updateUser(data)
        } catch (error) {
          console.error('Failed to refresh user profile:', error)
        }
      }
      refreshUserProfile()
    }
  }, [application?.status, updateUser])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.reason.trim()) {
      toast.error('Please provide a reason for your application')
      return
    }

    setIsSubmitting(true)
    try {
      await developerApplicationAPI.submit(formData)
      toast.success('Application submitted successfully!')
      refetch()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to submit application')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--color-background)' }}>
        <DashboardHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--color-primary)' }}></div>
        </div>
        <DashboardFooter />
      </div>
    )
  }

  // If application exists
  if (application && application.status !== 'none') {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--color-background)' }}>
        <DashboardHeader />
        
        <div className="flex-1 py-8 px-4">
          <div className="max-w-3xl mx-auto">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 mb-6 text-sm hover:opacity-70 transition"
              style={{ color: 'var(--color-primary)' }}
            >
              <ArrowLeft size={16} />
              Back to Dashboard
            </button>

            <div className="rounded-lg p-8" style={{ backgroundColor: 'var(--color-surface)' }}>
              <div className="flex items-center gap-3 mb-6">
                {application.status === 'pending' && (
                  <div className="flex items-center gap-2">
                    <Clock size={40} className="text-yellow-500" />
                    <div>
                      <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                        Application Pending
                      </h2>
                      <p style={{ color: 'var(--color-text-secondary)' }}>
                        Your developer application is under review
                      </p>
                    </div>
                  </div>
                )}

                {application.status === 'approved' && (
                  <div className="flex items-center gap-2">
                    <CheckCircle size={40} className="text-green-500" />
                    <div>
                      <h2 className="text-2xl font-bold text-green-600">
                        Application Approved! 🎉
                      </h2>
                      <p style={{ color: 'var(--color-text-secondary)' }}>
                        You now have developer access
                      </p>
                    </div>
                  </div>
                )}

                {application.status === 'rejected' && (
                  <div className="flex items-center gap-2">
                    <XCircle size={40} className="text-red-500" />
                    <div>
                      <h2 className="text-2xl font-bold text-red-600">
                        Application Rejected
                      </h2>
                      <p style={{ color: 'var(--color-text-secondary)' }}>
                        Your application was not approved
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                    Reason for Application
                  </label>
                  <div className="p-3 rounded bg-gray-50 dark:bg-gray-700/50" style={{ color: 'var(--color-text-secondary)' }}>
                    {application.reason}
                  </div>
                </div>

                {application.experience && (
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                      Experience
                    </label>
                    <div className="p-3 rounded bg-gray-50 dark:bg-gray-700/50" style={{ color: 'var(--color-text-secondary)' }}>
                      {application.experience}
                    </div>
                  </div>
                )}

                {application.portfolio_url && (
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                      Portfolio URL
                    </label>
                    <a
                      href={application.portfolio_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {application.portfolio_url}
                    </a>
                  </div>
                )}

                {application.github_url && (
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                      GitHub URL
                    </label>
                    <a
                      href={application.github_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {application.github_url}
                    </a>
                  </div>
                )}

                {application.status === 'rejected' && application.rejection_reason && (
                  <div>
                    <label className="block text-sm font-medium mb-1 text-red-600">
                      Rejection Reason
                    </label>
                    <div className="p-3 rounded bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200">
                      {application.rejection_reason}
                    </div>
                  </div>
                )}
              </div>

              {application.status === 'approved' && (
                <button
                  onClick={() => navigate('/developer')}
                  className="w-full px-6 py-3 text-white rounded-lg hover:opacity-90 transition"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                >
                  Go to Developer Portal
                </button>
              )}

              {application.status === 'rejected' && (
                <button
                  onClick={() => {
                    refetch()
                    window.location.reload()
                  }}
                  className="w-full px-6 py-3 text-white rounded-lg hover:opacity-90 transition"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                >
                  Apply Again
                </button>
              )}
            </div>
          </div>
        </div>

        <DashboardFooter />
      </div>
    )
  }

  // Application form
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--color-background)' }}>
      <DashboardHeader />
      
      <div className="flex-1 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 mb-6 text-sm hover:opacity-70 transition"
            style={{ color: 'var(--color-primary)' }}
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>

          <div className="rounded-lg p-8" style={{ backgroundColor: 'var(--color-surface)' }}>
            <div className="flex items-center gap-3 mb-6">
              <Code size={40} style={{ color: 'var(--color-primary)' }} />
              <div>
                <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                  Apply to Become a Developer
                </h1>
                <p style={{ color: 'var(--color-text-secondary)' }}>
                  Build and publish apps for the Dashit.io community
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                  Why do you want to become a developer? <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'var(--color-background)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                    '--tw-ring-color': 'var(--color-primary)'
                  } as any}
                  placeholder="Tell us why you want developer access..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                  Development Experience
                </label>
                <textarea
                  value={formData.experience}
                  onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'var(--color-background)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                    '--tw-ring-color': 'var(--color-primary)'
                  } as any}
                  placeholder="Describe your development experience, languages you know, past projects, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                  Portfolio URL
                </label>
                <input
                  type="url"
                  value={formData.portfolio_url}
                  onChange={(e) => setFormData({ ...formData, portfolio_url: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'var(--color-background)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                    '--tw-ring-color': 'var(--color-primary)'
                  } as any}
                  placeholder="https://yourportfolio.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                  GitHub Profile URL
                </label>
                <input
                  type="url"
                  value={formData.github_url}
                  onChange={(e) => setFormData({ ...formData, github_url: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'var(--color-background)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                    '--tw-ring-color': 'var(--color-primary)'
                  } as any}
                  placeholder="https://github.com/yourusername"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !formData.reason.trim()}
                className="w-full px-6 py-3 text-white rounded-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        </div>
      </div>

      <DashboardFooter />
    </div>
  )
}


